import { useRouter } from 'next/router';
import { useEffect, useMemo, useState } from 'react';

import { useHeading } from 'hooks/useHeading';
import { ManagedHeading } from 'ui/components/ManagedHeading';
import { Hreflangs } from 'ui/components/Hreflangs';
import { availablePages } from 'utils/hardcodedHreflangs';
import { Page } from 'ui/components/Page';
import { ProductList } from 'ui/components/ProductList';
import { Breadcrumb } from 'ui/components/Breadcrumb';
import { constructCategoryPath } from 'utils/constructCategoryPath';
import { createCategoryPageBreadcrumbLinks } from 'utils/breadcrumbRedirects';
import { Prose } from 'utils/serializeBlockContent';
import { PositionedTile } from '__generated__/graphql';
import { ProductListItem } from 'ui/components/ProductListItem';
import { ScrollToTopButton } from 'ui/elements/ScrollToTopButton';
import { PageHead } from 'ui/components/PageHead';
import { useCategoryPLPQuery } from 'gql/queries/plp';
import { MAX_PRODUCT_PAGE_SIZE } from 'utils/products';
import { useTranslate } from 'hooks/useTranslations';
import { ProductListFilterProvider } from 'ui/components/ProductListFilterProvider';
import { SanityContentSlot } from 'ui/components/SanityContentSlot';
import { isInitialClientRender } from 'utils/isInitialClientRender';
import { filterActivated } from 'utils/filterActivated';
import { useSiteConfig } from 'hooks/useSiteConfig';
import { getPreviewInput } from 'utils/getPreviewInput';
import { getUrlByCategory } from 'utils/getUrlByCategory';
import { RelatedLinks } from 'ui/components/RelatedLinks';
import { useIsCrawler } from 'hooks/useIsCrawler';
import { usePLPContent } from 'ui/pages/ProductListingPage/hooks/usePLPContent';

import { PaginatedProductListPage } from '../../components/PaginatedProductListPage';

import { usePlpEventTracking } from './hooks/usePlpEventTracking';
import CrawlersPagination from './components/CrawlersPagination';

export const ProductListingPage = () => {
  const router = useRouter();
  const t = useTranslate();

  const {
    staticFeatures: { isPNARegion },
  } = useSiteConfig();
  const isCrawler = useIsCrawler();

  const desiredCategory = router.query.category as string[];
  const categoryUrl = getUrlByCategory(desiredCategory);

  const [query, refetch] = useCategoryPLPQuery({
    router,
    offset: 0,
    includeMetadata: true,
    url: categoryUrl,
    src: router.query.src as string,
    preview: getPreviewInput(router.query),
  });

  const { isPLPref } = useHeading();
  if (isPLPref) {
    isPLPref.current = true;
  }

  const categoryGeneralId = query.data?.categoryByUrl?.id;

  useEffect(() => {
    // Refetch this query on the client within the context of a customer if:
    // 1. is the first render after SSR.
    // 2. user is not a crawler (we've inferred some important queries are being blocked in CSR as in GSC returns a 404 not found page -
    // we add this condition to prevent updating data if user is a Crawler).
    const shouldRefetch = isInitialClientRender(router) && !isCrawler;
    if (shouldRefetch) {
      refetch({ requestPolicy: 'cache-and-network' });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const currentCategory = query.data?.categoryByUrl;
  const categoryPath = useMemo(
    () => constructCategoryPath(currentCategory),
    [currentCategory]
  );

  const totalCount = currentCategory?.products.totalCount ?? 0;

  const path = useMemo(
    () => createCategoryPageBreadcrumbLinks(currentCategory),
    [currentCategory]
  );
  const pageParam = router.query.page;
  const serverPage = Number(pageParam) > 1 ? Number(pageParam) : 1;

  const hasActiveFilter = !!query.operation?.variables?.filters.length;
  const hasExplicitSort = !!query.operation?.variables?.sort;

  const {
    topContent,
    bottomContent,
    bottomSeoContent,
    localeBottomSeoContent,
    tiles,
    seo,
    recommended,
    recommendationsWithDetails,
    pageContentId,
    recommenderName,
    relatedLinks,
  } = usePLPContent(
    categoryUrl,
    currentCategory,
    hasActiveFilter,
    hasExplicitSort
  );

  const bottomSeoContentData = localeBottomSeoContent || bottomSeoContent;

  const queryOffset = Number(router.query.offset) || 0;

  const offset =
    totalCount && queryOffset >= totalCount
      ? totalCount - MAX_PRODUCT_PAGE_SIZE
      : queryOffset;

  const numberOfPages =
    pageParam && !offset
      ? serverPage * MAX_PRODUCT_PAGE_SIZE >= totalCount
        ? Math.ceil(totalCount / MAX_PRODUCT_PAGE_SIZE)
        : serverPage
      : Math.ceil(offset / MAX_PRODUCT_PAGE_SIZE) + 1;

  const pages = new Array(numberOfPages).fill(null);

  const totalProductsCount = currentCategory?.products.totalCount ?? 0;
  const totalCountToShow = totalProductsCount + recommended.length;
  const showLoadingIndicator =
    '/' + (desiredCategory as string[]).join('/') !== currentCategory?.url ||
    (offset !== 0 && query.fetching && !query.data);
  const [isCompactMode, setIsCompactMode] = useState<boolean>(false);

  const {
    handleIntersection,
    setListProducts,
    intersectedProductsRef,
    intersectAllProductsRef,
  } = usePlpEventTracking({
    categoryPath,
    currentCategory,
    isPlpFetching: query.fetching,
    offset,
    recommenderName,
  });

  const indexValue = pageParam ? true : seo?.noIndex;
  const flagPNARecommended = isPNARegion && filterActivated(router.query);

  return (
    <>
      <PageHead
        title={
          seo?.pageTitle
            ? seo.pageTitle
            : currentCategory?.seo?.pageTitle ??
              t('pageTitle', {
                title: '',
              })
        }
        description={
          seo?.pageDescription
            ? seo.pageDescription
            : currentCategory?.seo?.pageDescription ?? ''
        }
        openGraphDescription={seo?.openGraphDescription ?? ''}
        openGraphImageUrl={seo?.openGraphImageUrl ?? ''}
        openGraphTitle={seo?.openGraphTitle ?? ''}
        openGraphType={seo?.openGraphType ?? ''}
        openGraphVideoUrl={seo?.openGraphVideoUrl ?? ''}
        twitterDescription={seo?.twitterDescription ?? ''}
        twitterImageUrl={seo?.twitterImageUrl ?? ''}
        twitterTitle={seo?.twitterTitle ?? ''}
        noIndex={indexValue}
        noIndexFollow={indexValue}
        noHreflangs={availablePages.includes(categoryGeneralId || '')}
      />
      <Hreflangs id={categoryGeneralId} />
      <Page
        query={{
          ...query,
          fetching: showLoadingIndicator,
        }}
        emarsysData={{
          skipTracking: false,
        }}
        bloomreachData={{
          cat: currentCategory?.name,
          cat_id: currentCategory?.id,
        }}
      >
        <ProductListFilterProvider
          isFetching={query.fetching}
          noResults={totalCount === 0}
          filteringOptions={currentCategory?.products?.filteringOptions || []}
          sortingOptions={currentCategory?.products.sortingOptions || []}
          defaultSortOption={currentCategory?.products.selectedSort || ''}
          totalCount={totalCountToShow}
        >
          <Breadcrumb path={path} />
          <SanityContentSlot
            items={topContent}
            slotId="topContent"
            documentId={pageContentId}
          />
          <ManagedHeading
            className="text-2xl md:text-3xl lg:text-4xl xl:text-4xl uppercase font-bold"
            dataTestId="product-results"
            tag="h1"
            mainHeading={true}
          >
            <span>{currentCategory?.title || currentCategory?.name}</span>
          </ManagedHeading>
          <ProductList
            isFetching={query.fetching}
            noResults={totalCount === 0}
            filteringOptions={currentCategory?.products?.filteringOptions || []}
            sortingOptions={currentCategory?.products.sortingOptions || []}
            defaultSortOption={currentCategory?.products.selectedSort || ''}
            totalCount={totalCountToShow}
            handleCompactMode={setIsCompactMode}
          >
            {!flagPNARecommended &&
              recommended?.map((p, i) => (
                <ProductListItem
                  key={p.id}
                  position={offset + i + 1}
                  categoryId={currentCategory?.id}
                  fromRecommender={recommendationsWithDetails}
                  itemListId={currentCategory?.id}
                  itemListName={currentCategory?.name}
                  {...p}
                />
              ))}
            {pages.map((_, i) => {
              const isFirstPage = i === 0;
              const isLastPage = i === pages.length - 1;
              return !pageParam || (pageParam && i >= serverPage - 1) ? (
                <PaginatedProductListPage
                  isFirst={(isFirstPage && !pageParam) || serverPage - 1 === i}
                  isLast={isLastPage}
                  totalProducts={totalCount}
                  key={i}
                  offset={i * MAX_PRODUCT_PAGE_SIZE}
                  tiles={tiles as PositionedTile[] | null}
                  query={null}
                  productsRef={intersectedProductsRef}
                  allProductsRef={intersectAllProductsRef}
                  handleIntersection={handleIntersection}
                  setListProducts={setListProducts}
                  isCompactMode={isCompactMode}
                />
              ) : null;
            })}
          </ProductList>
          <CrawlersPagination totalProductsCount={totalCount} />
          {relatedLinks && <RelatedLinks relatedLinks={relatedLinks} />}
          <SanityContentSlot
            items={bottomContent}
            slotId="bottomContent"
            documentId={pageContentId}
          />
          {bottomSeoContentData && <Prose content={bottomSeoContentData} />}
        </ProductListFilterProvider>
        <ScrollToTopButton />
      </Page>
    </>
  );
};
